import {
    Box,
    Flex,
    IconDelete,
    IconDownload,
    IconInfoBold,
    ImageButton,
    Tooltip,
    toast,
} from '@gmg/gmg-react-components';
import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDownloadLicenseUpdate } from 'src/graphql/useDownloadLicenseUpdate';
import { useRemoveLicenseSystemFromOrg } from 'src/graphql/useRemoveLicenseSystemFromOrg';
import { getDonwloadLicenseUpdateEvent } from 'src/tracking';
import { LicenseSystem } from 'src/viewModels/LicenseSystem';
import styled from 'styled-components';
import AppContext from '../../AppContext';
import IconKey from '../../assets/svg/icon_key_filled.svg';
import IconMoveLicenses from '../../assets/svg/icon_request_licenseTransfer.svg';
import IconRepair from '../../assets/svg/icon_repair.svg';
import IconRequestTemporaryLs from '../../assets/svg/icon_request_temporary_ls.svg';
import RequestTemporaryLicensesModalContainer from '../appService/RequestTemporaryLicensesModalContainer';
import LicenseSystemInfoModalContainer from './LicenseSystemInfoModalContainer';
import RepairLicenseSystemModalContainer from './repairLicenseSystem/RepairLicenseSystemModalContainer';
import TransferLicenseSystemModalContainer from './swap-licenses/TransferLicenseSystemModalContainer';


export interface TargetLicenseSystemData extends Pick<LicenseSystem, 'id' | 'userName' | 'location' | 'isTransferSupported' | 'lastSeenDate'> { }
export interface LicenseManagementActionActionProps {
    isRequestTemporaryLicensesButtonShown: boolean;
    isTransferButtonShown: boolean;
    isTransferSupported: boolean;
    targetLicenseSystems: Array<TargetLicenseSystemData>;
    isLicenseSystemUpdateAvailable: boolean;
    isAllowedToUseLicenseSystemsOffline: boolean;
    licenseSystemId: string;
}

const LicenseManagementAction: FC<LicenseManagementActionActionProps> = (props) => {
    const { t } = useTranslation();
    const context = useContext(AppContext);
    const { orgId } = useParams();
    const removeLicenseSytem = useRemoveLicenseSystemFromOrg(props.licenseSystemId, orgId!);
    const [isDownloadBtnClicked, setIsDownloadBtnClicked] = useState(false);
    const { fetchStatus, isSuccess } = useDownloadLicenseUpdate(props.licenseSystemId, orgId!, isDownloadBtnClicked);

    useEffect(() => {
        if (isSuccess && fetchStatus !== 'fetching') {
            setIsDownloadBtnClicked(false);
        }
    }, [isSuccess, fetchStatus]);

    // When is which button shown?

    // delete: always
    // request temporary licenses: license system must really be empty (also no expired licenses)
    // transfer licenses: license system must be of type "online", must have at least one not expired license

    const handleDelete = () => {
        const pendingPromise = removeLicenseSytem.mutateAsync();

        void toast.promise(pendingPromise, {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: t('LicenseManagementAction.remove_license.toast_success_desc', 'Succesfully removed license system from your organization'),
            error: () => {
                return t('Common.Toast.Error', 'An error occurred');
            },
        });
    };

    const handleDeleteIconClick = () => {
        context.onShowModal('infoModal', {
            title: t('LicenseManagementAction.remove_license.modal_title_title', 'Delete License System'),
            content:
                <Box maxWidth='560px' className="px12_400_normal">
                    {t('LicenseManagementAction.remove_license.question_desc', "Are you sure you want to delete this license system? Deleting it will also remove the user's access from ColorProof GO.")}
                </Box>,
            onOkClick: () => { handleDelete(); },
            btnOkCaption: t('ButtonTitles.action_delete_btn', 'Delete'),
        });
    };

    const handleTemporaryLicensesIconClick = (serialNumber: string) => {
        return context.onShowModal('inputModal', {
            title: t('RequestTemporaryLicenses.Modal_title', 'Request temporary licenses'),
            titleIcon: <IconKey />,
            content: <RequestTemporaryLicensesModalContainer
                temporaryLicensesSerialNumber={serialNumber}
            />,
        });
    };
    const handleMoveLicensesIconClick = () => {
        return context.onShowModal('inputModal', {
            title: t('MoveLicensesModal.modal_title_title', 'Transfer licenses'),
            titleIcon: <IconMoveLicenses />,
            content: <TransferLicenseSystemModalContainer
                targetLicenseSystems={props.targetLicenseSystems}
                sourceLicenseSystemId={props.licenseSystemId}
                orgId={orgId!}
            />,
        });
    };
    const handleRepairLicensesIconClick = () => {
        return context.onShowModal('inputModal', {
            title: t('LicenseManagementAction.repair_license_system_btn', 'Repair license system'),
            titleIcon: <IconRepair />,
            content: <RepairLicenseSystemModalContainer selectedLicenseSystemId={props.licenseSystemId} />,
        });
    };

    const handleDownloadLicenseUpdateIconClick = () => {
        context.trackEvent(getDonwloadLicenseUpdateEvent());
        setIsDownloadBtnClicked(true);
    };

    const handleLicenseInfoIconClick = () => {
        return context.onShowModal('inputModal', {
            title: t('LicenseSystemInfoModal.modal_title_title', 'License System details'),
            content: <LicenseSystemInfoModalContainer licenseSystemId={props.licenseSystemId} orgId={orgId!} />,
        });
    };

    const isDownloadLicenseSystemUpdateButtonShown = props.isAllowedToUseLicenseSystemsOffline && props.isLicenseSystemUpdateAvailable;
    return (
        <Flex gap='16px' justifyContent='flex-end' width='100%' pr='20px' className='invisibleSVG'>
            <Tooltip message={t('LicenseSystemInfoModal.modal_title_title', 'License System details')}>
                <StyledButton
                    icon={<IconInfoBold />}
                    aria-label={`License info button for ${props.licenseSystemId}`}
                    onClick={() => {
                        handleLicenseInfoIconClick();
                    }}
                />
            </Tooltip>
            {isDownloadLicenseSystemUpdateButtonShown &&
                <Tooltip message={t('LicenseManagementAction.download_license_system_update_btn', 'Download latest license update')}>
                    <StyledButton
                        icon={<IconDownload />}
                        onClick={() => { handleDownloadLicenseUpdateIconClick(); }}
                        aria-label={`Download license update for ${props.licenseSystemId}`}
                    />
                </Tooltip>
            }
            {props.isAllowedToUseLicenseSystemsOffline &&
                <Tooltip message={t('LicenseManagementAction.repair_license_system_btn', 'Repair license system')}>
                    <StyledButton
                        icon={<IconRepair />}
                        onClick={() => { handleRepairLicensesIconClick(); }}
                        aria-label={`Repair license system ${props.licenseSystemId}`}
                    />
                </Tooltip>
            }
            {props.isTransferButtonShown &&
                <Tooltip message={t('MoveLicensesModal.modal_title_title', 'Transfer license system')}>
                    <StyledButton
                        icon={<IconMoveLicenses />}
                        onClick={() => { handleMoveLicensesIconClick(); }}
                        aria-label="Move licenses"
                    />
                </Tooltip>
            }
            {props.isRequestTemporaryLicensesButtonShown &&
                <Tooltip message={t('LicenseManagementAction.request_btn', 'Request temporary licenses')}>
                    <StyledButton
                        icon={<IconRequestTemporaryLs />}
                        onClick={() => { handleTemporaryLicensesIconClick(props.licenseSystemId); }}
                        aria-label="Request temporary licenses"
                    />
                </Tooltip>
            }
            <Tooltip message={t('LicenseManagementAction.delete_btn', 'Delete license system')}>
                <StyledButton
                    icon={<IconDelete />}
                    onClick={() => { handleDeleteIconClick(); }}
                    aria-label="Delete license system"
                />
            </Tooltip>
        </Flex>
    );
};

const StyledButton = styled(ImageButton)`
    cursor: pointer;
`;

export default LicenseManagementAction;